"use client";

import React, { useEffect, useRef, useState } from "react";
import { CalendarHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ScheduleATour() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="134276" id="visit-our-home" className="bg-background text-foreground relative overflow-hidden py-24 px-6 sm:py-32">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-30">
        <div className="bg-[#ffbd00]/10 h-[40rem] w-[40rem] rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`relative mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <h2 className="font-sans text-primary mb-6 text-4xl font-bold tracking-tight sm:text-5xl">
          Experience the Moonrise Warmth
        </h2>
        
        <p className="text-foreground/80 mx-auto mb-10 max-w-2xl text-lg font-medium leading-relaxed sm:text-xl">
          Discover a sanctuary of care in McKinney, Texas. We invite you to tour our boutique residential home, meet our compassionate team, and see how we create a loving, family-focused environment for every resident.
        </p>

        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          {}
          <Button asChild size="lg" className="btn-primary">
            <Link to="/ContactUsPage">
              <CalendarHeart className="mr-2 h-5 w-5" />
              Schedule Your Visit
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}